

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewOrderCommModal = ({
    isOpen,
    onClose,
    onSave,
    travelOrderId = null
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        travel_order_id: travelOrderId || '',
        order_pax_id: '',
        email: '',
        description: '',
        status: 'Sent',
        template_id: '',
        last_sent_by_id: user?.id || ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [travelOrders, setTravelOrders] = useState([]);
    const [orderPax, setOrderPax] = useState([]);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [loadingOrderPax, setLoadingOrderPax] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            if (!travelOrderId) {
                fetchTravelOrders();
            }
        }
    }, [isOpen, travelOrderId]);

    useEffect(() => {
        if (formData.travel_order_id) {
            fetchOrderPax(formData.travel_order_id);
        }
    }, [formData.travel_order_id]);

    const fetchTravelOrders = async () => {
        setLoadingTravelOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingTravelOrders(false);
        }
    };

    const fetchOrderPax = async (orderId) => {
        setLoadingOrderPax(true);
        try {
            const response = await axiosInstance.get(`/travel-order-pax/?travel_order_id=${orderId}`);
            setOrderPax(response.data || []);
        } catch (error) {
            console.error('Error fetching order pax:', error);
        } finally {
            setLoadingOrderPax(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Comm Name is required';
        if (!formData.travel_order_id) errors.travel_order_id = 'Travel Order is required';
        if (!formData.order_pax_id) errors.order_pax_id = 'Passenger is required';
        if (!formData.email) errors.email = 'Email is required';
        if (!formData.description) errors.description = 'Description is required';
        if (!formData.template_id) errors.template_id = 'Template ID is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                travel_order_id: parseInt(formData.travel_order_id),
                order_pax_id: parseInt(formData.order_pax_id),
                last_sent_by_id: formData.last_sent_by_id ? parseInt(formData.last_sent_by_id) : null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                travel_order_id: travelOrderId || '',
                order_pax_id: '',
                email: '',
                description: '',
                status: 'Sent',
                template_id: '',
                last_sent_by_id: user?.id || ''
            });
        } catch (error) {
            console.error('Error saving Order Comm:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Order Comm',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Order Communication</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Comm Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                {!travelOrderId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.travel_order_id} required>
                                            <InputLabel>Travel Order</InputLabel>
                                            <Select
                                                name="travel_order_id"
                                                value={formData.travel_order_id}
                                                onChange={handleInputChange}
                                                label="Travel Order"

                                                disabled={loadingTravelOrders}
                                            >
                                                <MenuItem value=""><em>Select a Travel Order</em></MenuItem>
                                                {travelOrders.map(to => (
                                                    <MenuItem key={to.id} value={to.id}>{to.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.travel_order_id && <MDTypography variant="caption" color="error">{formErrors.travel_order_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.order_pax_id} required>
                                        <InputLabel>Passenger</InputLabel>
                                        <Select
                                            name="order_pax_id"
                                            value={formData.order_pax_id}
                                            onChange={handleInputChange}
                                            label="Passenger"

                                            disabled={loadingOrderPax || !formData.travel_order_id}
                                        >
                                            <MenuItem value=""><em>Select a Passenger</em></MenuItem>
                                            {orderPax.map(op => (
                                                <MenuItem key={op.id} value={op.id}>{op.passenger_name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.order_pax_id && <MDTypography variant="caption" color="error">{formErrors.order_pax_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={!!formErrors.email}
                                        helperText={formErrors.email}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Sent">Sent</MenuItem>
                                            <MenuItem value="Failed">Failed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Template ID"
                                        name="template_id"
                                        value={formData.template_id}
                                        onChange={handleInputChange}
                                        error={!!formErrors.template_id}
                                        helperText={formErrors.template_id}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={4}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        error={!!formErrors.description}
                                        helperText={formErrors.description}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Order Communication'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewOrderCommModal;

